package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * One line of the spend report.
 *
 * **Gross, refunded and net as three numbers.** `releaseCredits` decrements the counter it
 * incremented, so a month with 900 spent and 400 refunded is indistinguishable from one with 500
 * spent if you only keep the total — and refunds happen on every failed provider call and every
 * reaped job, so that is the normal case rather than the exception.
 */
@Serializable
data class SpendRowDto(
    val projectSlug: String? = null,
    val projectName: String? = null,
    val locale: String? = null,
    val provider: String? = null,
    val strings: Int = 0,
    val sourceChars: Int = 0,
    val creditsGross: Int = 0,
    val creditsRefunded: Int = 0,
    /**
     * Work paid for by the workspace's own provider key.
     *
     * Rendered with no cost column at all rather than a zero: a zero in a money column reads as
     * "free to us", and this is "not ours to count".
     */
    val byok: Boolean = false,
) {
    val creditsNet: Int get() = creditsGross - creditsRefunded
}

/**
 * The report over one window.
 *
 * The window is stated as an explicit **UTC** range, on the page and in the CSV header, because
 * `credit_usage.period` is a UTC month and an admin in UTC+9 exporting "October" otherwise gets
 * numbers matching neither their experience nor their invoice.
 */
@Serializable
data class SpendReportDto(
    val fromUtc: String,
    val toUtc: String,
    val rows: List<SpendRowDto> = emptyList(),
    /**
     * Set when the window reaches back before the ledger existed.
     *
     * Those months have a monthly total and nothing to break it down by, and saying so is the only
     * honest option — a silent gap reads as "we spent nothing" and a plausible-looking split would
     * be a number somebody takes into a budget meeting.
     */
    val truncatedBefore: String? = null,
) {
    val creditsNet: Int get() = rows.sumOf { it.creditsNet }
}

/** How the report is grouped. One table, three ways of reading it. */
@Serializable
enum class SpendGrouping { PROJECT, LOCALE, PROVIDER }
